<template>
  <button v-bind="buttonAttrs" v-on="attrs">
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue"
const attrs = useAttrs()

interface ButtonProps {
  theme?: "LIGHT" | "DARK" | "CART"
  size?: "sm" | "md" | "lg" | "nopadding" | "audio"
  outlined?: boolean
  border?: boolean
  shadow?: boolean
  variant?: "default" | "primary" | "secondary"
  type?: "button" | "submit" | "reset"
  disabled?: boolean,
  id?: string
}

const buttonProps = withDefaults(defineProps<ButtonProps>(), {
  outlined: false,
  border: true,
  shadow: true, 
})

const buttonId = computed(() => {
  return buttonProps.id || "button-id"
})

const buttonSizeClasses = computed(() => {
  switch (buttonProps.size) {
    case 'lg':
      return 'font-bold text-[1.143rem] py-[0.714rem] px-[1.429rem]'
    case 'md':
      return 'font-bold text-[1.143rem] py-[0.714rem] px-[1.429rem]'
    case 'nopadding':
      return 'font-bold text-[0.71rem] !w-auto'
    case 'audio':
    return "font-semibold text-[1rem] p-3"
    default:
      return 'font-bold text-[1rem] py-[0.429rem] px-[1.143rem]'
  }
})

const variantClasses: Record<Exclude<ButtonProps["variant"], undefined>, string> = {
  default: "bg-gray-800 text-white hover:bg-gray-600 disabled:bg-gray-500",
  primary: "bg-ash-500 rounded-md text-white hover:bg-gray-700",
  secondary:
    "font-normal shadow-[0_0.5rem_0.8571rem_0_rgba(0,0,0,0.21)] hover:text-gold-100 h-[3.5714rem] w-[21.4286rem] hover:border-gray-500 hover:border rounded-[0.428571rem] border-gray-300 border"
}

const getOutlinedClasses = (theme: ButtonProps["theme"], removeBorder: boolean) => {
  const base =
    theme === "DARK"
      ? ["bg-transparent text-white", "hover:bg-transparent", "hover:text-gray-500", "disabled:text-gray-700"]
      : ["bg-transparent text-gray-800", "hover:bg-transparent", "hover:text-gray-600", "disabled:text-gray-500", "disabled:bg-transparent"]

  const borderColor = theme === "DARK" ? "white" : "gray-800"
  const hoverBorderColor = theme === "DARK" ? "gray-500" : "gray-600"
  const disabledBorderColor = theme === "DARK" ? "gray-700" : "gray-500"

  if (!removeBorder) {
    base.push(`border border-${borderColor}`, `hover:border-${hoverBorderColor}`, `disabled:border-${disabledBorderColor}`)
  }

  return base.join(" ")
}

const getFilledClasses = (theme: ButtonProps["theme"]) => {
  if (theme === "DARK") {
    return "text-gray-700 bg-white hover:text-gray-600"
  }
  if (theme === "CART") {
    return "bg-ash-500 text-white hover:bg-ash-800 disabled:bg-ash-300"
  }
  return "bg-gray-800 text-white hover:bg-gray-600 disabled:bg-gray-500"
}

const buttonColorClasses = computed(() => {
  const { variant, theme, outlined, border } = buttonProps

  if (variant && variantClasses[variant]) return variantClasses[variant]

  const removeBorder = !border
  return outlined ? getOutlinedClasses(theme, removeBorder) : getFilledClasses(theme)
})

const buttonAttrs = computed(() => {
  const classes = [
    buttonSizeClasses.value,
    buttonColorClasses.value
  ]

  const attrs: Record<string, any> = {
    id: buttonId.value,
    class: classes.join(" ")
  }

  if (buttonProps.disabled != null) attrs.disabled = buttonProps.disabled

  return attrs
})
</script>
 